// Alpine controller for the area Discover section's POI board takeover pane.
//
// The board renders one card per Discover fact in an ordered,
// drag-reorderable list. Dragging a card reorders the `pois` array and
// emits ONE whole-value cms-image-changed autosave — the fact-id array — to
// `orderTarget` (the SAME area:<slug>:discover overlay target the section's
// own prose fields autosave to; editor.ts's _targetRoute merges every field
// key posted to that target into one content object, so this `order` field
// lands as discover.order without a dedicated overlay/target).
//
// Mirrors static/js/cms/amenity-grouping.ts / surrounding-picker.ts for the
// config-parse shape, the Sortable init-in-$nextTick + data-sortable-bound
// idempotence pattern, and the cms-image-changed emit pattern — but this
// widget has no modal/transactional step: every drag emits immediately
// (matching the section-rail's reorder, not the amenity-grouping modal's
// confirm/discard).
//
// T2.8b (this slice) added the org-shared write zone — addPOI/
// deleteEverywhere are STILL raw fetch calls straight to the T2.7 REST
// endpoints (never cms-image-changed/editor.ts's autosave queue — creating/
// hard-deleting an operator-origin fact is an authoritative, org-wide
// mutation, not a per-site content edit), following the same CSRF +
// processResponseTriggers contract as image-picker.ts's removeFromPool /
// video-picker.ts's remove / curation-modal.ts's save: a raw fetch() never
// auto-processes the server's HX-Trigger response header, so
// processResponseTriggers(res) re-dispatches it on document.body — that's
// what drives cms_editor.templ's self-refresh wrapper (hx-trigger=
// "area-poi-added,area-poi-removed from:body") to re-fetch
// the leftpane with the server's authoritative state. On a non-2xx response
// (HandleServiceError's JSON error body, no HX-Trigger to process) the
// caught branch surfaces the server's translated `message` via
// dispatchToast, falling back to the generic `actionFailedMsg` config string
// (a network failure / non-JSON body has no server message to show).
//
// T1.3 (this slice) REMOVED setCategory/setGeo/setImage's raw-fetch writes
// (T2.7 PATCH/POST /facts) — those clobbered the operator's edit on the
// board's own self-refresh (rootcause-poi-board.md Bug 1b: the refresh
// re-renders from the content doc's stale discover.items projection, which
// never saw the org-row write) and never armed Publish/updated the preview
// (Bug 3, since the content doc never changed). All three now write through
// the SAME per-site item_override overlay channel as updateOverride
// (title/description) — see _emitOverride below. `canManageAreas` still
// gates them (the templ still renders their inputs only in the org zone;
// widening that zone to every website-CMS actor is a later task).
//
// T2.8c (this slice) adds:
//   - search/filter: matchesSearch/matchesCategory are pure predicates,
//     originally read from :class bindings in the templ to DIM (not remove)
//     non-matching cards, so SortableJS's drag-index math (which reads DOM
//     position) stayed correct regardless of the active filter. T2.4
//     (below) supersedes the dim-not-hide half of that design.
//   - the per-site prose zone: updateOverride(id, field, value) dispatches ONE
//     cms-image-changed per field to a PER-CARD target
//     ("area:<areaSlug>:item_override:<factId>", built by
//     _itemOverrideTarget) — editor.ts's dedicated item_override
//     _targetRoute branch nests it under discover.item_overrides.<factId>,
//     never the shared discoverTarget (which can't express per-fact keys —
//     see CMSPOIBoardData's doc comment).
//   - the per-site hide toggle: toggleHide(id) mutates a local hiddenIds
//     array and emits the WHOLE array as one whole-value autosave to
//     hiddenTarget (mirrors _emitOrder's whole-value contract) — merges into
//     discover.hidden via editor.ts's existing generic "area:<slug>:<section>"
//     branch (hiddenTarget IS the same "area:<slug>:discover" string
//     orderTarget uses, so both fields land in the same PUT).
//
// T2.9 (this slice) wires the org-zone IMAGE slot to a SHARED, area-filtered
// cmsImagePicker instance (the templ's `pool` config — parsed once here,
// unused until now — is exactly that area's image pool):
//   - openImage(id) sets activePoiId, which mounts (via the templ's
//     <template x-if="activePoiId">) a FRESH cmsImagePicker seeded from THAT
//     POI's current image (activePoiImageIds()) and immediately shows itself
//     (x-init="open()" in the templ). It forces a null → id transition on
//     EVERY call — even re-opening the same id — because Alpine's x-if only
//     reacts to falsy⇄truthy changes, not value changes: reopening id→id
//     (same truthy value) would otherwise reuse the ALREADY-MOUNTED instance
//     from the first open, silently ignoring a different POI's data if the
//     user managed to trigger it (defense against a future UI path, not
//     reachable through today's modal-blocks-the-page flow).
//   - The picker mount now sets field 'image_id' and target
//     "area:<slug>:item_override:<factId>" DIRECTLY, so its own
//     cms-image-changed is routed and WRITTEN by editor.ts's root listener —
//     independent of this board component. (Previously the mount used a raw
//     "poi_image:<factId>" field/target that editor.ts's _targetRoute couldn't
//     route: it was dropped as "unrecognized target", and the save only
//     happened when onPoiImageChanged fired and RE-emitted a correctly-targeted
//     event. When the board's Alpine scope failed to bind, that handler didn't
//     fire and the pick silently didn't save — the intermittent-save bug.)
//   - onPoiImageChanged is now UI-only: it resolves the fact from activePoiId
//     (the picker is only mounted for the active POI) and updates the optimistic
//     card thumbnail via setImage. It performs no write and no stopPropagation.
//
// T2.4 (this slice) supersedes T2.8c's dim-not-hide filter design
// (rootcause-poi-board.md Bug 4, the stated option 1): the templ now
// `x-show`s each card on `isVisible(poi.id)` instead of dimming via
// `:class`, so a non-matching card is actually removed from layout/tab
// order, not just grayed out. That reintroduces the exact risk the dim
// design was chosen to avoid — SortableJS's onEnd index math
// (applySortableReorder) reads DOM position via `[data-poi]` querySelectorAll,
// and x-show'd-out cards keep their DOM node (display:none) but a drag
// gesture can't originate from a hidden node anyway, while a drag started
// on a VISIBLE card would still compute oldIndex/newIndex over a rows list
// that skips the hidden ones only visually, not in the DOM — so index math
// would still be wrong the moment any card is filtered out. Instead of
// relying on that, this slice disables the Sortable instance outright
// whenever a filter is active (filterActive, backed by search/
// categoryFilter via an init()-time $watch pair calling
// _syncSortableDisabled()) and re-enables it once both are cleared — so
// drag is simply unavailable while the list is filtered, never computed
// against a partial view.

import Sortable from "sortablejs";
import { applySortableReorder, createReorderable } from "../shared/sortable-reorder";
import { parseJsonOr } from "../shared/utils";
import { dispatchToast, getCSRFToken, processResponseTriggers } from "../shared/htmx-helpers";
import { externalHref, normalizeExternalUrl } from "./normalize-url";
import { studioConfirm } from "../shared/studio-confirm";

// One POI card — the wire shape cms_poi_board.templ's cmsPOIBoardXData emits
// (a flat JSON-tagged projection of components.CMSPOICard).
export interface POIItem {
  id: string;
  title: string;
  description: string;
  category: string;
  // url is this fact's optional external link (T10) — per-site
  // discover.item_overrides.<factId>.url overlay, same shape/emit path as
  // title/description (updateOverride below).
  url?: string;
  lat?: number;
  lng?: number;
  image_id?: string;
  image_url?: string;
  // hidden seeds the board's local hiddenIds set on load — this site's
  // discover.hidden membership for this fact (T2.8c).
  hidden?: boolean;
  // map_hidden (B2b) is this site's discover.item_overrides.<factId>.
  // map_hidden overlay — per-site "hide this POI's map marker while keeping
  // its card" toggle, written via updateOverride below (same chokepoint as
  // title/description/url), distinct from `hidden` above.
  map_hidden?: boolean;
  // audiences/seasons (T3.5) are this site's per-fact target-audience/season
  // overlay — discover.item_overrides.<factId>.{audiences,seasons}, applied
  // server-side by ApplyDiscoverItemOverrides before this board reads the
  // item. A POI's seasons membership is also the POI→season-card linkage: a
  // POI shows under a season card when its seasons overlay includes that
  // season. Optional on the wire (server omits when empty, T2.8a's
  // omitempty convention); cmsPOIBoard's constructor normalizes both to `[]`
  // (see `pois` below) so the org-zone checkbox group always binds to a real
  // array, never undefined.
  audiences?: string[];
  seasons?: string[];
}

export interface CMSPOIBoardConfig {
  pois: POIItem[];
  // category enum → localized label, resolved server-side (areaDiscoverCategoryLabel)
  // so the widget never needs to call Go's t() at runtime.
  categoryLabels: Record<string, string>;
  areaSlug: string;
  // areaID is the area's domain.AreaID — the T2.7 org-write endpoints
  // (/api/areas/{id}/facts…) are keyed by area ID, never slug.
  areaID: string;
  // areaAPIBase is the org-scoped API prefix those endpoints are mounted
  // under ("/organizations/{orgSlug}/api/areas") — see
  // CMSPOIBoardData.AreaAPIBase's doc comment for why this can't be derived
  // from baseURL (website-scoped) client-side. Every org-write URL is built
  // as `${areaAPIBase}/${areaID}/facts…`.
  areaAPIBase: string;
  baseURL: string;
  // Autosave routing targets (editor.ts's cms-image-changed → _queueEdit
  // path). All three currently resolve to the same string
  // (service.CMSTargetAreaSection(slug, "discover")) — see
  // CMSPOIBoardData's doc comment for why they're kept separate here anyway.
  discoverTarget: string;
  orderTarget: string;
  hiddenTarget: string;
  canManageAreas: boolean;
  // Area-filtered image pool, JSON string — fed straight into the shared
  // T2.9 image-slot picker's `pool` config (already-parsed array, see
  // cmsPOIBoard's `pool` field below) so the picker never re-parses it.
  pool: string;
  // Generic fallback toast message for a failed org write (network error, or
  // a non-2xx response with no JSON `message`) — see addPOI/deleteEverywhere
  // (_writeFact's remaining callers as of T1.3).
  actionFailedMsg: string;
}

export function cmsPOIBoard(config: CMSPOIBoardConfig) {
  const pool = parseJsonOr(config.pool, [] as unknown[]);

  return {
    // audiences/seasons are normalized to `[]` here (T3.5) — not left
    // undefined — so the org-zone audiences/seasons checkbox group (each
    // checkbox's `:checked` reads `poi.audiences.includes(...)` /
    // `poi.seasons.includes(...)`) always binds to a real array from the
    // first render; a card whose overlay never set these keys would
    // otherwise carry `undefined` (the wire type is optional, see POIItem's
    // doc comment) and `.includes` would throw.
    pois: (config.pois || []).map((p) => ({
      ...p,
      audiences: p.audiences || [],
      seasons: p.seasons || [],
    })),
    categoryLabels: config.categoryLabels || {},
    areaSlug: config.areaSlug,
    areaID: config.areaID,
    areaAPIBase: config.areaAPIBase,
    baseURL: config.baseURL,
    discoverTarget: config.discoverTarget,
    orderTarget: config.orderTarget,
    hiddenTarget: config.hiddenTarget,
    canManageAreas: config.canManageAreas,
    pool,
    actionFailedMsg: config.actionFailedMsg || "",

    // Add-POI form state (T2.8b) — plain Alpine data, not a native <form>
    // FormData read: addPOI() consumes these two fields directly.
    newTitle: "",
    newCategory: "",

    // Search/filter state (T2.8c) — plain Alpine data bound by x-model in
    // the templ's search row. Read by matchesSearch/matchesCategory below;
    // never used to remove cards from `pois` (see file header comment).
    search: "",
    categoryFilter: "",

    // hiddenIds is this site's discover.hidden membership — seeded from each
    // POI's `hidden` flag (server-projected from discoverContent["hidden"])
    // so a refreshed board shows the correct toggle state without an extra
    // round trip. A plain string array (not a Set) so Alpine's reactivity
    // proxy tracks membership changes for isHidden()'s :show/:class readers.
    hiddenIds: (config.pois || []).filter((p) => p.hidden).map((p) => p.id) as string[],

    // expandedId is the accordion's single-open state (T4) — the fact id whose
    // edit body is expanded, null when every row is collapsed. The board renders
    // dense-by-default: each row's summary header (drag handle + thumbnail +
    // title + category) is the reorder unit, its collapsible body the edit
    // surface. Single-open (opening one row collapses any other, see
    // toggleExpand) keeps the list dense so drag-reorder stays short. Reset to
    // null on every fresh mount — a board self-refresh after add/delete re-inits
    // this component — so the refreshed list always opens collapsed.
    expandedId: null as string | null,

    // activePoiId is the T2.9 image-slot's mount switch — non-null while the
    // shared per-POI picker is open for that fact. Read by the templ's
    // <template x-if="activePoiId"> to mount/tear down the picker's own
    // x-data (see openImage's doc comment for why every open forces a fresh
    // mount rather than reusing whatever instance is already there).
    activePoiId: null as string | null,

    // The live Sortable instance, so destroy() can tear it down.
    _sortable: null as Sortable | null,

    // Bound pool-image-uploaded listener ref, so destroy() can detach it
    // (mirrors image-picker.ts's _onPoolUpload).
    _onPoolUpload: null as ((e: Event) => void) | null,

    // Alpine lifecycle. Bound in $nextTick so the x-for card list has
    // rendered before Sortable queries it (mirrors amenity-grouping.ts /
    // surrounding-picker.ts).
    init() {
      (this as any).$nextTick(() => this._initSortable());
      // T2.4: re-sync the Sortable disabled state whenever either filter
      // input changes (the watcher ignores the new-value arg and reads
      // this.filterActive fresh — mirrors schedule-builder.ts's
      // multi-$watch-into-one-handler pattern).
      (this as any).$watch("search", () => this._syncSortableDisabled());
      (this as any).$watch("categoryFilter", () => this._syncSortableDisabled());
      // Bug fix: a fresh upload made FROM INSIDE the shared per-POI image
      // picker (T2.9) lands in the PICKER's own pool copy
      // (cmsImagePicker._handlePoolUpload → its _allPool) via this same
      // window event, but the board holds a SEPARATE `pool` array (seeded
      // once from the templ's area-filtered `pool` config) that never
      // learns about it. Without this listener, setImage's
      // `this.pool.find(e => e.id === imageId)` misses the just-uploaded
      // id, so the optimistic image_url update (T7, see setImage) silently
      // no-ops and the board thumbnail stays blank until a full reload —
      // even though the overlay write itself succeeds. Mirrors
      // image-picker.ts's _handlePoolUpload dedup-by-id; the board doesn't
      // need the picker's auto-select/pending-count bookkeeping, only
      // enough of the entry shape (id + url) for setImage to resolve it.
      // `typeof window !== "undefined"` guards the same as
      // deleteEverywhere's window.confirm check below — this file runs
      // under test without a DOM global present.
      if (typeof window !== "undefined") {
        this._onPoolUpload = (e: Event) => this._handlePoolUpload((e as CustomEvent).detail);
        window.addEventListener("pool-image-uploaded", this._onPoolUpload);
      }
    },

    destroy() {
      this._sortable?.destroy();
      this._sortable = null;
      if (this._onPoolUpload && typeof window !== "undefined") {
        window.removeEventListener("pool-image-uploaded", this._onPoolUpload);
      }
      this._onPoolUpload = null;
    },

    // _handlePoolUpload adds a freshly-uploaded image to the board's OWN
    // pool copy so setImage (called right after via onPoiImageChanged, once
    // the picker emits cms-image-changed for the same upload) can resolve
    // the id and set the optimistic image_url. Dedup by id — mirrors
    // image-picker.ts's _handlePoolUpload; unlike that picker-side handler,
    // this one doesn't filter by field/target (every upload, from any
    // picker on the page, is a candidate the board might immediately need
    // to resolve) and tracks no pending-upload/spinner state (that's the
    // picker's job, not the board's).
    _handlePoolUpload(detail: { id: string; url: string }) {
      if (!detail || !detail.id) return;
      if (this.pool.some((e: any) => e && e.id === detail.id)) return;
      this.pool.push({ id: detail.id, url: detail.url });
    },

    // categoryLabel resolves a POI's category enum to its localized label,
    // falling back to the raw value for an unrecognized/empty category
    // (mirrors area_detail.templ's areaDiscoverCategoryLabel default branch).
    categoryLabel(cat: string): string {
      return this.categoryLabels[cat] || cat;
    },

    // matchesSearch/matchesCategory are the search-row filter predicates
    // (T2.8c), read by isVisible below. As of T2.4 a non-matching card is
    // HIDDEN — the templ `x-show`s each card on isVisible(poi.id) rather than
    // dimming it via :class — and drag-reorder is disabled outright whenever
    // either filter is active (filterActive/_syncSortableDisabled), so
    // SortableJS's DOM-position index math is never computed against a
    // partial view. `pois` itself is never filtered/reordered by these two
    // predicates — they're pure read-only membership checks.
    matchesSearch(id: string): boolean {
      const q = this.search.trim().toLowerCase();
      if (!q) return true;
      const poi = this.pois.find((p) => p.id === id);
      return !!poi && poi.title.toLowerCase().includes(q);
    },

    matchesCategory(id: string): boolean {
      if (!this.categoryFilter) return true;
      const poi = this.pois.find((p) => p.id === id);
      return !!poi && poi.category === this.categoryFilter;
    },

    // isVisible (T2.4) is the templ's card `x-show` predicate — a card shows
    // only when it matches BOTH the search text and the category filter.
    // Supersedes the old opacity-40 :class dim (see the file header comment).
    isVisible(id: string): boolean {
      return this.matchesSearch(id) && this.matchesCategory(id);
    },

    // filterActive (T2.4) is true whenever either filter input is non-empty
    // — read by init()'s $watch pair (via _syncSortableDisabled) to disable
    // drag-reorder while the board shows a partial view.
    get filterActive(): boolean {
      return this.search.trim() !== "" || this.categoryFilter !== "";
    },

    // _syncSortableDisabled (T2.4) toggles the live Sortable instance's
    // `disabled` option to match filterActive — called from init()'s
    // search/categoryFilter $watch pair. No-ops before Sortable has bound
    // (e.g. a watch firing before $nextTick's _initSortable has run).
    _syncSortableDisabled() {
      this._sortable?.option("disabled", this.filterActive);
    },

    // isHidden reports this site's current discover.hidden membership for a
    // fact — read by the templ's title/description dim styling and the
    // hide-toggle button's label swap.
    isHidden(id: string): boolean {
      return this.hiddenIds.includes(id);
    },

    // isExpanded (T4) is the accordion body's x-show predicate — true for the
    // single currently-open row (see expandedId).
    isExpanded(id: string): boolean {
      return this.expandedId === id;
    },

    // toggleExpand (T4) flips this row's accordion body — the summary header's
    // @click / Enter-Space handler. Single-open: assigning a new id collapses
    // whatever was open, clicking the open row closes it. Reordering is
    // unaffected — the drag handle (data-poi-handle) stops propagation so a
    // drag never toggles, and the header stays visible whether open or closed.
    toggleExpand(id: string) {
      this.expandedId = this.expandedId === id ? null : id;
    },

    // Idempotent: guarded via data-sortable-bound so a re-entrant call (e.g.
    // a future slice re-initializing after an HTMX-driven board refresh)
    // never double-binds the same list.
    _initSortable() {
      const el = (this as any).$refs.poiList as HTMLElement | undefined;
      if (!el) return;
      if (el.dataset.sortableBound === "1") return;
      el.dataset.sortableBound = "1";
      this._sortable = createReorderable(el, {
        handle: "[data-poi-handle]",
        draggable: "[data-poi]",
        onEnd: (e: Sortable.SortableEvent) => {
          this.pois = applySortableReorder(el, e, this.pois, "data-poi-id");
          this._emitOrder();
        },
      });
      // Seed the `disabled` option from the CURRENT filter state right away —
      // unreachable today (search/categoryFilter both start "" on a fresh
      // mount), but a future same-node re-init that preserves an already-
      // active filter (e.g. an HTMX-driven board refresh) must not mount a
      // freshly-ENABLED Sortable over a partially-hidden (x-show'd) list.
      this._syncSortableDisabled();
    },

    // Emit ONE whole-value cms-image-changed event carrying the reordered
    // fact-id array — editor.ts's _queueEdit debounces + flushes it into the
    // discover.order overlay key at this.orderTarget.
    _emitOrder() {
      const root = (this as any).$root as HTMLElement;
      root.dispatchEvent(
        new CustomEvent("cms-image-changed", {
          detail: {
            field: "order",
            target: this.orderTarget,
            value: this.pois.map((p) => p.id),
          },
          bubbles: true,
        }),
      );
    },

    // _itemOverrideTarget builds this fact's per-site prose autosave target —
    // "area:<areaSlug>:item_override:<factId>" — routed by editor.ts's
    // dedicated item_override _targetRoute branch into
    // discover.item_overrides.<factId>. Built from areaSlug directly (NOT
    // from discoverTarget/"area:<slug>:discover" — that shared string can't
    // carry a per-fact key; see CMSPOIBoardData's doc comment).
    _itemOverrideTarget(id: string): string {
      return `area:${this.areaSlug}:item_override:${id}`;
    },

    // _emitOverride dispatches ONE per-site item_override field write —
    // "area:<areaSlug>:item_override:<factId>", built by _itemOverrideTarget
    // — as cms-image-changed (this widget carries no data-cms-field, see the
    // file/templ header comments) so editor.ts's existing debounced
    // _queueEdit/_flush pipeline picks it up; multiple fields edited within
    // the debounce window merge into ONE PUT (editor.ts groups pending edits
    // by target). Shared chokepoint for updateOverride (title/description),
    // setCategory, setGeo (flat lat/lng, R3), and setImage (T1.3) — all four
    // are now per-site content-overlay writes, not org-authoritative fact
    // mutations.
    _emitOverride(id: string, field: string, value: unknown) {
      const root = (this as any).$root as HTMLElement;
      root.dispatchEvent(
        new CustomEvent("cms-image-changed", {
          detail: { field, target: this._itemOverrideTarget(id), value },
          bubbles: true,
        }),
      );
    },

    // updateOverride records ONE per-site field edit for a single fact — the
    // per-site zone's TextField/TextArea @change handler (title/description/
    // url) plus, as of B2b, the "show on map" checkbox's @change handler
    // (map_hidden, a bool rather than a string value).
    updateOverride(
      id: string,
      field: "title" | "description" | "url" | "map_hidden",
      value: string | boolean,
    ) {
      this._emitOverride(id, field, value);
    },

    // saveUrl is the url field's @change (blur) handler: it auto-formats a bare
    // "example.com" / "www.example.com" into "https://…" (preserving the host,
    // never touching an existing scheme), reflects the corrected value back into
    // the input via the x-model binding, then persists through updateOverride —
    // so what the operator sees in the field is exactly what renders as a link.
    saveUrl(poi: POIItem) {
      poi.url = normalizeExternalUrl(poi.url ?? "");
      this.updateOverride(poi.id, "url", poi.url);
    },

    // previewHref backs the small "open link" affordance under the url field: a
    // safe http(s) href, or null when the value isn't a usable external link
    // (so the affordance hides). Mirrors the render-time gate.
    previewHref(raw: string | undefined): string | null {
      return externalHref(raw ?? "");
    },

    // toggleHide flips this fact's per-site discover.hidden membership — the
    // hide-toggle button's @click handler — then emits the WHOLE hiddenIds
    // array as one whole-value autosave (mirrors _emitOrder's contract:
    // discover.hidden is a flat fact-id array overlay key, not a per-fact
    // patch, so every toggle re-sends the complete set).
    toggleHide(id: string) {
      const idx = this.hiddenIds.indexOf(id);
      if (idx === -1) this.hiddenIds.push(id);
      else this.hiddenIds.splice(idx, 1);
      this._emitHidden();
    },

    _emitHidden() {
      const root = (this as any).$root as HTMLElement;
      root.dispatchEvent(
        new CustomEvent("cms-image-changed", {
          detail: { field: "hidden", target: this.hiddenTarget, value: [...this.hiddenIds] },
          bubbles: true,
        }),
      );
    },

    // activePoiImageIds seeds the shared picker's `selected` config from the
    // CURRENTLY-active POI's image_id (empty when unset). Read by the templ's
    // x-data string at picker-mount time, mirroring cmsImagePickerXData's
    // selJSON for every other image field.
    activePoiImageIds(): string[] {
      const poi = this.pois.find((p) => p.id === this.activePoiId);
      return poi?.image_id ? [poi.image_id] : [];
    },

    // openImage opens the SHARED per-POI image picker (T2.9) targeted at
    // `id` — the org-zone "Edit image" button's @click handler. No-ops when
    // canManageAreas is false (defense in depth, same as every other org
    // write here; the templ never renders the calling button in that case).
    //
    // Always nulls activePoiId first and re-sets it on the next tick — even
    // when re-opening the SAME id — so the templ's <template x-if
    // ="activePoiId"> (which Alpine only re-evaluates on a falsy⇄truthy
    // transition, never on a value change) destroys and freshly recreates
    // the picker's x-data on every call. Without this, opening POI B right
    // after POI A (both truthy ids) would reuse POI A's already-mounted
    // picker instance, silently showing/editing the wrong fact's image.
    openImage(id: string) {
      if (!this.canManageAreas) return;
      this.activePoiId = null;
      (this as any).$nextTick(() => {
        this.activePoiId = id;
      });
    },

    // onPoiImageChanged handles the shared picker's cms-image-changed emit
    // (its @cms-image-changed listener in the templ). The picker now emits the
    // item_override target directly (field 'image_id', target
    // 'area:<slug>:item_override:<factId>' — see the templ mount), so editor.ts's
    // root cms-image-changed listener performs the WRITE on its own, regardless
    // of whether THIS handler (or the whole board component) is live. This
    // handler is therefore purely a UI concern: the optimistic thumbnail update.
    // It reads the fact id from activePoiId (the picker is only mounted for the
    // active POI) rather than parsing the field, which is now the cell key.
    onPoiImageChanged(e: Event) {
      const ce = e as CustomEvent<{ value?: unknown }>;
      const factId = this.activePoiId;
      const imageId = typeof ce.detail?.value === "string" ? ce.detail.value : "";
      this.activePoiId = null;
      if (!factId || !imageId) return; // closed/saved with nothing selected
      this.setImage(factId, imageId);
    },

    // setImage optimistically reflects the picked image on the card's reactive
    // `poi.image_url`. It NO LONGER performs the overlay write: the picker's own
    // cms-image-changed already carries the item_override target
    // (discover.item_overrides.<factId>.image_id), so editor.ts's root listener
    // writes it — reliably, even when this board component didn't initialize.
    // (Previously setImage re-emitted via _emitOverride, which meant the write
    // only happened if this handler fired; when the board's Alpine scope failed
    // to bind, the picker's raw 'poi_image:<id>' target hit editor.ts unrouted
    // and was dropped — "unrecognized target" — so the pick silently didn't
    // save.) Kept here (T7): the async write never round-trips a fresh
    // image_url, so without this optimistic set the thumbnail stayed stale until
    // a reload. Resolved from `pool` by id, preferring optimized_url to match
    // the picker tile; left unchanged (not blanked) on a pool miss.
    setImage(factId: string, imageId: string) {
      if (!this.canManageAreas) return;
      const poi = this.pois.find((p) => p.id === factId);
      const entry = this.pool.find((e: any) => e && e.id === imageId) as
        { url?: string; optimized_url?: string } | undefined;
      if (poi && entry) poi.image_url = entry.optimized_url || entry.url;
    },

    // _factURL builds one fact's T2.7 endpoint URL: <org-scoped
    // areaAPIBase>/<areaID>/facts[/<factID>].
    _factURL(factID?: string): string {
      const base = `${this.areaAPIBase}/${encodeURIComponent(this.areaID)}/facts`;
      return factID ? `${base}/${encodeURIComponent(factID)}` : base;
    },

    // _writeFact is the shared org-write chokepoint: every addPOI/
    // deleteEverywhere call funnels through here so the CSRF header,
    // error-toast fallback, and HX-Trigger re-dispatch stay in ONE place.
    // (T1.3 moved setCategory/setGeo/setImage off this path onto
    // _emitOverride — they're per-site content-overlay writes now, not
    // org-authoritative fact mutations.) Returns true on success (2xx) so a
    // caller can do a write-specific follow-up (addPOI clears the form;
    // deleteEverywhere needs no follow-up — the area-poi-removed trigger
    // drives the refresh).
    async _writeFact(
      method: "POST" | "PATCH" | "DELETE",
      url: string,
      body?: Record<string, unknown>,
    ): Promise<boolean> {
      try {
        const headers: Record<string, string> = { "X-CSRF-Token": getCSRFToken() };
        if (body) headers["Content-Type"] = "application/json";
        const res = await fetch(url, {
          method,
          credentials: "same-origin",
          headers,
          body: body ? JSON.stringify(body) : undefined,
        });
        if (!res.ok) {
          const errBody = (await res.json().catch(() => ({}))) as { message?: string };
          dispatchToast(errBody.message || this.actionFailedMsg, "error");
          return false;
        }
        // A raw fetch() never auto-processes HX-Trigger — re-dispatch it on
        // document.body so the toast fires AND cms_editor.templ's self-
        // refresh wrapper (hx-trigger="area-poi-added,area-poi-removed
        // from:body") re-fetches the leftpane.
        processResponseTriggers(res);
        return true;
      } catch (err) {
        console.error("[poi-board] fact write failed", err);
        dispatchToast(this.actionFailedMsg, "error");
        return false;
      }
    },

    // setCategory writes a fact's per-site category override
    // (discover.item_overrides.<factId>.category, T1.3) — the category
    // <select>'s @change handler. No-ops when canManageAreas is false
    // (defense in depth; the templ never renders the calling <select> in
    // that case). Replaces the old T2.7 PATCH /facts/{id} org write
    // (AreaService.UpdatePOIFields), which clobbered on self-refresh (Bug
    // 1b) and never touched the content doc (Bug 3).
    setCategory(id: string, category: string) {
      if (!this.canManageAreas) return;
      this._emitOverride(id, "category", category);
    },

    // setGeo writes a fact's per-site lat/lng override
    // (discover.item_overrides.<factId>.{lat,lng}, T1.3) — the org-shared
    // geo inputs' @change handler. FLAT keys (R3), not a nested geo object —
    // matches discover.items[]'s own top-level lat/lng shape
    // (buildDiscoverItem) so ApplyDiscoverItemOverrides can copy straight
    // across. Up to two separate _emitOverride calls (editor.ts's
    // _queueEdit merges same-target field writes into ONE overlay object, so
    // both still land in a single PUT); a null/undefined value is skipped
    // rather than sent, so an unset field never turns into a stray `null`
    // overlay leaf.
    setGeo(id: string, lat: number | null, lng: number | null) {
      if (!this.canManageAreas) return;
      if (lat !== null && lat !== undefined) this._emitOverride(id, "lat", lat);
      if (lng !== null && lng !== undefined) this._emitOverride(id, "lng", lng);
    },

    // toggleInArray flips `value`'s membership in `arr` IN PLACE (splice/
    // push, not a reassignment) — the org-zone audiences/seasons checkbox
    // group's shared @change helper (each checkbox calls this against
    // `poi.audiences`/`poi.seasons` for the option it represents, then
    // forwards the whole array via setAudiences/setSeasons). In-place
    // mutation matters here: `poi.audiences`/`poi.seasons` are properties of
    // an object inside Alpine's `pois` x-for array, and Alpine's reactivity
    // proxy tracks the array's own indices/length — mutating in place keeps
    // every other binding reading `poi.audiences` (e.g. the checkboxes'
    // `:checked`) in sync without a manual re-render trigger.
    toggleInArray(arr: string[], value: string) {
      const idx = arr.indexOf(value);
      if (idx === -1) arr.push(value);
      else arr.splice(idx, 1);
    },

    // setAudiences writes a fact's per-site target-audience override
    // (discover.item_overrides.<factId>.audiences, T3.5) — called after the
    // org-zone audiences checkbox group's @change handler has already
    // flipped the relevant value via toggleInArray. Forwards the WHOLE
    // array through the shared item_override overlay chokepoint — mirrors
    // setCategory/setGeo's write shape, never a partial/toggle write.
    setAudiences(id: string, audiences: string[]) {
      if (!this.canManageAreas) return;
      this._emitOverride(id, "audiences", audiences);
    },

    // setSeasons writes a fact's per-site season override
    // (discover.item_overrides.<factId>.seasons, T3.5) — called after the
    // org-zone seasons checkbox group's @change handler has already flipped
    // the relevant value via toggleInArray. This overlay is also the
    // POI→season-card linkage: a POI shows under a season card on the
    // rendered site when its seasons overlay includes that season. Same
    // whole-array write shape as setAudiences.
    setSeasons(id: string, seasons: string[]) {
      if (!this.canManageAreas) return;
      this._emitOverride(id, "seasons", seasons);
    },

    // addPOI creates a new operator-origin fact (T2.7 AddPOI, POST /facts
    // { title, category }) from the inline add-POI form's newTitle/
    // newCategory Alpine state (no native FormData — mirrors cmsRepeater's
    // addRow(), the codebase convention for CMS list mutations). Clears the
    // form on success; the area-poi-added HX-Trigger drives the board
    // refresh (cms_editor.templ's self-refresh wrapper), so this method
    // does not touch `this.pois` itself.
    async addPOI() {
      if (!this.canManageAreas) return;
      const title = this.newTitle.trim();
      const category = this.newCategory;
      if (!title || !category) return;
      const ok = await this._writeFact("POST", this._factURL(), { title, category });
      if (ok) {
        this.newTitle = "";
        this.newCategory = "";
      }
    },

    // deleteEverywhere hard-deletes an operator-origin fact from every site
    // in the org (T2.7 DeletePOI, DELETE /facts/{id}) — the "Delete
    // everywhere" button's @click handler. Reads its confirm message from
    // the CLICKED element's own data-poi-delete-confirm attribute (set by
    // the templ per-button, not a shared $root dataset — see
    // cms_poi_board.templ). An empty/missing confirm message (or no
    // `window`, e.g. this file under test) skips the confirm entirely.
    async deleteEverywhere(id: string, ev?: Event) {
      if (!this.canManageAreas) return;
      const target = ev?.currentTarget as HTMLElement | undefined;
      const confirmMsg = target?.dataset?.poiDeleteConfirm || "";
      if (confirmMsg && !(await studioConfirm(confirmMsg))) return;
      void this._writeFact("DELETE", this._factURL(id));
    },
  };
}
